import { setSparkflowGlobalContext } from '@thoughtspot/blink-context';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import { Horizontal } from '@thoughtspot/radiant-react/widgets/layout';
import { isFirstLogin } from '@thoughtspot/session-service';
import React, { useEffect, useRef, useState } from 'react';
import { useBlinkContextForSparkflow } from "../../contexts/sparkflow-blink-context";
import { getSparkflowConfig } from "../../contexts/sparkflow-context";
import { useSparkflowNavService } from "../../services/nav-service/nav-service";
import { useGetSpotbuildGeneratedIds, useSaveSpotbuildGeneratedIds, } from "../../services/onboarding.service";
import { useUpdateLastSelectedDataSources } from "../../services/user-admin/user-admin-service";
import { buildCompleted, buildInfoChanged, buildPageErrorShown, buildPageLoaded, buildScreenCtaClicked, previewDataScrolled, } from "../../utils/metrics/metrics-utils";
import { BUILD_PHASE } from "../../utils/sparkflow-constants";
import { BuildPanel } from './components/build-panel';
import { ImagePanel } from './components/image-panel';
import { PreviewTablePanel } from './components/preview-table-panel';
import { useSpotbuild } from './hooks/use-spotbuild';
import styles from './spotbuild-integration.module.scss';
import { isCompletedPhase } from './utils/build-helpers';
export const SpotbuildIntegration = () => {
    const spotbuild = useSpotbuild();
    const { buildPhase, progress, previewData, columns, liveboardId, modelId, tableId, buildErrorMessage, clearBuildError, functionLabel, industryLabel, navigateToOnboarding, navigateToAgentSelection, } = spotbuild;
    const { metricsService, sessionService } = useBlinkContextForSparkflow();
    const [bannerAlertMessage, setBannerAlertMessage] = useState({
        isVisible: false,
        errorMessage: '',
    });
    const { saveSpotbuildGeneratedIds, data, error, loading, } = useSaveSpotbuildGeneratedIds();
    const { updateLastSelectedDataSources, } = useUpdateLastSelectedDataSources();
    const { data: spotbuildGeneratedIdsData, error: spotbuildGeneratedIdsError, getSpotbuildGeneratedIds, } = useGetSpotbuildGeneratedIds();
    const { updateSparkflowGlobalContext } = setSparkflowGlobalContext();
    const sparkflowNavService = useSparkflowNavService();
    const isSparkflowUseCaseBasedOnboardingEnabled = sessionService.isSparkflowUsecaseBasedOnboardingEnabled();
    const sparkflowConfig = getSparkflowConfig();
    const selectedFunction = sparkflowConfig?.onboardingSelections?.selectedFunction || '';
    const selectedIndustry = sparkflowConfig?.onboardingSelections?.selectedIndustry || '';
    const dismissedErrorRef = useRef(null);
    const hasScrolledRef = useRef(false);
    useEffect(() => {
        buildPageLoaded(metricsService, selectedFunction, selectedIndustry);
    }, []);
    useEffect(() => {
        if (buildPhase === BUILD_PHASE.COMPLETED) {
            buildCompleted(metricsService, selectedFunction, selectedIndustry);
        }
    }, [buildPhase]);
    useEffect(() => {
        const apiError = buildErrorMessage ||
            error?.message ||
            spotbuildGeneratedIdsError?.message;
        if (apiError && apiError !== dismissedErrorRef.current) {
            setBannerAlertMessage({ isVisible: true, errorMessage: apiError });
            buildPageErrorShown(metricsService, selectedFunction, selectedIndustry, apiError);
        }
        else if (!apiError) {
            dismissedErrorRef.current = null;
            setBannerAlertMessage(prev => prev.isVisible ? { isVisible: false, errorMessage: '' } : prev);
        }
    }, [buildErrorMessage, error, spotbuildGeneratedIdsError]);
    const handleBannerClose = () => {
        dismissedErrorRef.current = bannerAlertMessage.errorMessage;
        setBannerAlertMessage({ isVisible: false, errorMessage: '' });
        clearBuildError();
    };
    const onContinueClick = () => {
        if (buildPhase === BUILD_PHASE.COMPLETED &&
            liveboardId &&
            modelId &&
            tableId) {
            saveSpotbuildGeneratedIds([
                {
                    liveboard_identifier: liveboardId,
                    industry: spotbuild.industry || selectedIndustry,
                    function: spotbuild.function || selectedFunction,
                    data_sources: [
                        {
                            model_identifier: modelId,
                            table_identifiers: [tableId],
                        },
                    ],
                },
            ]);
            if (!isFirstLogin()) {
                updateLastSelectedDataSources([modelId]);
            }
        }
        buildScreenCtaClicked(metricsService, selectedFunction, selectedIndustry);
    };
    const handleNavigateToOnboarding = () => {
        navigateToOnboarding();
        buildInfoChanged(metricsService, selectedFunction, selectedIndustry);
    };
    const handlePreviewDataScrolled = () => {
        if (!hasScrolledRef.current) {
            hasScrolledRef.current = true;
            previewDataScrolled(metricsService, selectedFunction, selectedIndustry);
        }
    };
    useEffect(() => {
        if (data !== null && data !== undefined && !loading) {
            getSpotbuildGeneratedIds();
        }
    }, [data, loading, getSpotbuildGeneratedIds]);
    const removeTypename = (obj) => {
        if (Array.isArray(obj)) {
            return obj.map(removeTypename);
        }
        if (obj !== null && typeof obj === 'object') {
            const newObj = {};
            Object.keys(obj).forEach(key => {
                if (key !== '__typename') {
                    newObj[key] = removeTypename(obj[key]);
                }
            });
            return newObj;
        }
        return obj;
    };
    useEffect(() => {
        if (spotbuildGeneratedIdsData) {
            const spotbuildGeneratedIdsDataWithoutTypename = removeTypename(spotbuildGeneratedIdsData);
            updateSparkflowGlobalContext({
                spotbuildGenResponse: spotbuildGeneratedIdsDataWithoutTypename,
            });
            if (isSparkflowUseCaseBasedOnboardingEnabled) {
                sparkflowNavService.goToUseCase();
            }
            else {
                navigateToAgentSelection();
            }
        }
    }, [spotbuildGeneratedIdsData]);
    return (React.createElement(React.Fragment, null,
        React.createElement(GlobalAlert.Banner, { type: BannerAlertType.ERROR, "data-testid": "spotbuild-banner-alert", message: bannerAlertMessage.errorMessage, isVisible: bannerAlertMessage.isVisible, onClose: handleBannerClose }),
        React.createElement(Horizontal, { className: styles.sparkflowContainer, grow: true },
            React.createElement(BuildPanel, { buildPhase: buildPhase, progress: progress, functionLabel: functionLabel, industryLabel: industryLabel, isSparkflowUseCaseBasedOnboardingEnabled: isSparkflowUseCaseBasedOnboardingEnabled, onNavigateToOnboarding: handleNavigateToOnboarding, onNavigateToAgentSelection: onContinueClick }),
            isCompletedPhase(buildPhase) ? (React.createElement(PreviewTablePanel, { data: previewData, columns: columns, onScrollCapture: handlePreviewDataScrolled })) : (React.createElement(ImagePanel, null)))));
};
//# sourceMappingURL=spotbuild-integration.container.js.map